import * as React from 'react';
import type { UserDocumentCollection } from '../../MedicareProAPI/Collections/User/UserDocumentCollection';
import type { UserDocument } from '../../MedicareProAPI/Entities/User/UserDocument';
import { UserDocumentRepository } from '../../MedicareProAPI/Repositories/User/UserDocumentRepository';

import StyledModal from '../Components/StyledModal';

interface UserDocumentListModalProps {
  userId: number;
  userAccessToken: string;
  open: boolean;
  onClose: () => void;
  onSave: (selectedDocuments: Array<any>) => void;
}

/**
 * Component Modal that lists documents that have been saved in the docs page
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const UserDocumentListModal = ({
  userId,
  userAccessToken,
  open,
  onClose,
  onSave,
}: UserDocumentListModalProps): React.JSX.Element => {
  const userDocumentRepository = new UserDocumentRepository();
  userDocumentRepository.setAccessToken(userAccessToken);

  const [userDocuments, setUserDocuments] = React.useState<Array<UserDocument>>([]);
  const [selectedDocuments, setSelectedDocuments] = React.useState<Array<any>>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  /**
   * Fetches user documents of type "file" for the given user,
   * and updates the document list and loading state.
   */
  const fetchData = (): void => {
    userDocumentRepository
      .getUserDocuments(userId, 'file')
      .then((response: UserDocumentCollection) => {
        setUserDocuments(response.getItems());
        setIsLoading(false);
      });
  };

  /**
   * Toggles a document in the selected documents list.
   * If the document is already selected, it removes it; otherwise, it adds it.
   *
   * @param document - The document object being toggled
   * @param index - The index of the document in the list (unused here but available)
   */
  const handleCheckboxToggle = (
    document: { getDocSystemName?: () => string; getDocFilename?: () => string },
    index: number
  ): void => {
    const docObject = {
      systemName: document.getDocSystemName?.(),
      fileName: document.getDocFilename?.(),
    };

    setSelectedDocuments((prev) => {
      const alreadySelected = prev.findIndex((d) => d.systemName === docObject.systemName);

      if (alreadySelected !== -1) {
        // Remove if already selected
        return prev.filter((_, i) => i !== alreadySelected);
      } else {
        // Add to selected documents
        return [...prev, docObject];
      }
    });
  };

  React.useEffect(() => {
    fetchData();
  }, [userId, userAccessToken]);

  return (
    <StyledModal size="lg" title="Documents" footerCloseButton={true} show={open} onClose={onClose}>
      <div className="row">
        <div className="col-12">
          {isLoading && (
            <>
              <i className="far fa-spinner fa-spin"></i> Loading docs...
            </>
          )}
          {!isLoading &&
            userDocuments.length > 0 &&
            userDocuments.map((document, index) => {
              const isChecked = selectedDocuments.some(
                (d) => d.systemName === document.getDocSystemName()
              );

              return (
                <div
                  className="col-12 file border-bottom pb-2 pt-2 w-100 cp calMess bg-contrast"
                  style={{ fontSize: '10pt' }}
                  key={index}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleCheckboxToggle(document, index)}
                  />
                  <i className="fal fa-file mr-2 ml-2" aria-hidden="true"></i>
                  {document.getDocFilename()}
                  <i style={{ fontSize: '9pt' }}>({document.getDocDate()})</i>
                </div>
              );
            })}
          {!isLoading && userDocuments.length === 0 && <>No files in your Docs page.</>}
        </div>
        {selectedDocuments.length > 0 && (
          <div className="col-12 mt-3">
            <div className="btn btn-sm btn-primary cp" onClick={() => onSave(selectedDocuments)}>
              Attach Selected
            </div>
          </div>
        )}
      </div>
    </StyledModal>
  );
};
